import { useEffect, useState } from 'react';
import { whatsAppDigits } from '@dunea/shared';
import { apiSaveWhatsApp, type WhatsAppInput, type WhatsAppSettings } from '~/lib/api/marketing';
import { classNames } from '~/utils/classNames';
import {
  CARD,
  CAPTION,
  CopyField,
  ErrorBanner,
  Field,
  Section,
  Select,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import { apiErrorMessage, whatsAppPhoneError, whatsAppPreview } from './marketing-model';

type Position = NonNullable<WhatsAppSettings['position']>;

interface Draft {
  enabled: boolean;
  phone: string;
  message: string;
  label: string;
  position: Position;
}

function draftFrom(s: WhatsAppSettings | null): Draft {
  return {
    enabled: s?.enabled ?? false,
    phone: s?.phone ?? '',
    message: s?.message ?? '',
    label: s?.label ?? '',
    position: s?.position ?? 'bottom-right',
  };
}

function toInput(d: Draft): WhatsAppInput | null {
  const phone = whatsAppDigits(d.phone);

  if (!phone) {
    // Turning the button off never needs a phone; turning it on does.
    return d.enabled ? null : { enabled: false };
  }

  return {
    enabled: d.enabled,
    phone,
    ...(d.message.trim() ? { message: d.message.trim() } : {}),
    ...(d.label.trim() ? { label: d.label.trim() } : {}),
    position: d.position,
  };
}

export function WhatsAppView({
  appId,
  settings,
  onSaved,
}: {
  appId: string;
  settings: WhatsAppSettings | null;
  onSaved: (next: WhatsAppSettings | null) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(settings));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => setDraft(draftFrom(settings)), [settings]);

  const phoneError = whatsAppPhoneError(draft.phone);
  const input = toInput(draft);
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftFrom(settings));
  const preview = whatsAppPreview(draft);

  const update = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setSaved(false);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!input || !dirty) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      onSaved(await apiSaveWhatsApp(appId, input));
      setSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the WhatsApp button.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="WhatsApp chat button" icon="i-ph:whatsapp-logo">
      <form onSubmit={save} className={classNames(CARD, 'space-y-3.5 p-4')}>
        <div className="space-y-1.5">
          <Switch
            checked={draft.enabled}
            onChange={(enabled) => update({ enabled })}
            label="Show a WhatsApp button on my site"
          />
          <p className="text-[11px] leading-relaxed text-muted">
            A floating button that opens a WhatsApp chat with you, with your message already typed. It appears on the
            published site after your next publish.
          </p>
        </div>

        <Field
          label="WhatsApp number"
          hint={
            phoneError ? (
              <span className="text-danger/90">{phoneError}</span>
            ) : (
              'Country code first, digits only, e.g. 919876543210 for +91 98765 43210.'
            )
          }
        >
          <input
            value={draft.phone}
            onChange={(e) => update({ phone: e.target.value })}
            inputMode="tel"
            spellCheck={false}
            placeholder="919876543210"
            aria-invalid={Boolean(phoneError)}
            className={classNames('ui-input-mono', phoneError && '!border-danger/50')}
          />
        </Field>

        <Field label="Pre-filled message">
          <textarea
            value={draft.message}
            onChange={(e) => update({ message: e.target.value })}
            maxLength={500}
            rows={2}
            placeholder="Hi! I have a question about…"
            className="ui-input resize-y"
          />
        </Field>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Button label">
            <input
              value={draft.label}
              onChange={(e) => update({ label: e.target.value })}
              maxLength={40}
              placeholder="Chat on WhatsApp"
              className="ui-input"
            />
          </Field>
          <Field label="Position">
            <Select value={draft.position} onChange={(v) => update({ position: v as Position })} label="Position">
              <option value="bottom-right">Bottom right</option>
              <option value="bottom-left">Bottom left</option>
            </Select>
          </Field>
        </div>

        <div className="space-y-1.5">
          <span className={CAPTION}>Preview</span>
          <div
            aria-hidden
            className={classNames(
              'flex h-20 items-end rounded-lg border border-hairline bg-sunken p-2.5',
              draft.position === 'bottom-left' ? 'justify-start' : 'justify-end',
            )}
          >
            <span className="inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/15 px-3 py-1.5 text-[11px] font-medium text-success">
              <div className="i-ph:whatsapp-logo-fill text-[13px]" />
              {preview.label}
            </span>
          </div>
          {preview.url && <CopyField value={preview.url} label="Chat link" />}
        </div>

        {error && <ErrorBanner message={error} />}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving || !dirty || !input}
            className="ui-btn-accent !px-4 !py-2 !text-[12px]"
          >
            {saving && <Spinner />}
            {saving ? 'Saving…' : 'Save button'}
          </button>
          {draft.enabled && !whatsAppDigits(draft.phone) && (
            <span className="text-[11px] text-muted">Add your number to turn it on.</span>
          )}
          {saved && !dirty && <span className="text-[11px] text-muted">Saved. Publish to apply.</span>}
        </div>
      </form>
    </Section>
  );
}
